import { type ConsentStatusType, type CustomerConsent } from "@repo/api";
import { t } from "@repo/translation";
import { type Locale } from "date-fns";

import { localizeDateAndTime } from "../../components/LocalizedDateAndTime";

export const getConsentMessage = (
  consentValues: (CustomerConsent & { status: ConsentStatusType })[],
  dateFnsLocale?: Locale
) => {
  const followupConsent = consentValues.find(
    (el) => el.consentType === "FOLLOWUP_CONSENT"
  );

  const promotionalConsent = consentValues.find(
    (el) => el.consentType === "PROMOTIONAL_CONSENT"
  );

  const consentMessageMap = {
    MISSING: {
      type: "MISSING",
      title: t("prompt.consent_missing"),
      body: t("prompt.consent_missing_desc"),
      variant: "cautionary",
    },
    FOLLOWUP_PENDING: {
      type: "PENDING",
      title: t("prompt.consent_pending"),
      body: followupConsent?.updatedAt
        ? t("prompt.consent_pending_desc_with_time", {
            time: localizeDateAndTime(
              followupConsent?.updatedAt,
              dateFnsLocale
            ),
          })
        : t("prompt.consent_pending_desc"),
      variant: "informative",
    },
    PROMOTION_PENDING: {
      type: "PENDING",
      title: t("prompt.consent_pending"),
      body: promotionalConsent?.updatedAt
        ? t("prompt.consent_pending_desc_with_time", {
            time: localizeDateAndTime(
              promotionalConsent?.updatedAt,
              dateFnsLocale
            ),
          })
        : t("prompt.consent_pending_desc"),
      variant: "informative",
    },
    FOLLOWUP_APPROVED: {
      type: "APPROVED",
      title: t("prompt.consent_approved"),
      body: t("prompt.consent_approved_desc"),
      variant: "positive",
    },
    PROMOTION_APPROVED: {
      type: "APPROVED",
      title: t("prompt.consent_approved"),
      body: t("prompt.promotional_consent_approved_desc"),
      variant: "positive",
    },
    FOLLOWUP_NOT_APPROVED: {
      type: "NOT_APPROVED",
      title: t("prompt.consent_not_approved"),
      body: t("prompt.consent_not_approved_desc"),
      variant: "negative",
    },
    PROMOTION_NOT_APPROVED: {
      type: "NOT_APPROVED",
      title: t("prompt.consent_not_approved"),
      body: t("prompt.promotional_consent_not_approved_desc"),
      variant: "negative",
    },
    ERROR: {
      type: "ERROR",
      title: t("prompt.consent_error"),
      body: t("prompt.consent_error_desc"),
      variant: "negative",
    },
  };

  // if there's only promotional consent
  if (promotionalConsent && !followupConsent) {
    if (promotionalConsent.status === "MISSING") {
      return consentMessageMap["MISSING"];
    }
    if (promotionalConsent.status === "PENDING") {
      return consentMessageMap["PROMOTION_PENDING"];
    }
    if (promotionalConsent.status === "APPROVED") {
      return consentMessageMap["PROMOTION_APPROVED"];
    }
    if (promotionalConsent.status === "NOT_APPROVED") {
      return consentMessageMap["PROMOTION_NOT_APPROVED"];
    }
    if (promotionalConsent.status === "ERROR") {
      return consentMessageMap["ERROR"];
    }

    // if there's only followup or both consents, only show default (followup) message
  } else if (followupConsent) {
    if (followupConsent?.status === "MISSING") {
      return consentMessageMap["MISSING"];
    }
    if (followupConsent?.status === "PENDING") {
      return consentMessageMap["FOLLOWUP_PENDING"];
    }
    if (followupConsent?.status === "APPROVED") {
      return consentMessageMap["FOLLOWUP_APPROVED"];
    }
    if (followupConsent?.status === "NOT_APPROVED") {
      return consentMessageMap["FOLLOWUP_NOT_APPROVED"];
    }
    if (followupConsent?.status === "ERROR") {
      return consentMessageMap["ERROR"];
    }
  } else {
    return consentMessageMap["MISSING"];
  }
};
